<!-- eslint-disable no-undef -->
<script setup lang="ts">
import { watch, onMounted, ref } from "vue";
import Config from "@/config/Config";

const props = defineProps({
  lat: Number,
  lng: Number,
  name: String,
  tag: String,
});

// Add TypeScript declarations for Google Maps API
declare global {
  interface Window {
    google: any;
  }
}

declare namespace google.maps {
  class Map {
    constructor(element: HTMLElement, options?: any);
    setCenter(latLng: any): void;
  }

  class Marker {
    constructor(options?: any);
    setMap(map: Map | null): void;
  }
}

declare namespace google.maps.marker {
  class AdvancedMarkerElement {
    constructor(options?: any);
    map: google.maps.Map | null;
  }
}

const tagName = `gmap${props.tag}`;
let map: google.maps.Map;
let marker: any; // Use any to handle both marker types
const isAdvancedMarkerSupported = ref(false);

// Check if AdvancedMarkerElement is supported
const checkAdvancedMarkerSupport = () => {
  return window.google && google.maps && google.maps.marker && google.maps.marker.AdvancedMarkerElement;
};

watch(
  () => props.tag,
  () => {
    const myLatLng = { lat: props.lat, lng: props.lng };

    if (marker) {
      if (isAdvancedMarkerSupported.value) {
        marker.map = null;
      } else if (marker instanceof google.maps.Marker) {
        (marker as unknown as google.maps.Marker).setMap(null);
      }
    }

    if (isAdvancedMarkerSupported.value) {
      marker = new google.maps.marker.AdvancedMarkerElement({
        position: myLatLng,
        title: props.name,
        map: map
      });
    } else {
      // Fallback to legacy Marker if AdvancedMarkerElement is not available
      const legacyMarker = new google.maps.Marker({
        position: myLatLng,
        title: props.name,
      });
      legacyMarker.setMap(map);
      marker = legacyMarker as unknown as google.maps.marker.AdvancedMarkerElement;
    }

    if (map) {
      map.setCenter(myLatLng);
    }
  }
);

onMounted(() => {
  const myLatLng = { lat: props.lat, lng: props.lng };
  const mapElement = document.getElementById(tagName);

  if (!mapElement) {
    console.error(`Map element with ID ${tagName} not found`);
    return;
  }

  map = new google.maps.Map(mapElement, {
    center: myLatLng,
    zoom: 12,
    gestureHandling: "none",
    mapTypeControlOptions: { mapTypeIds: [] },
    mapId: Config.googleMapsConfig.mapId, // Custom map ID for styled map
  });

  // Check if AdvancedMarkerElement is supported
  isAdvancedMarkerSupported.value = checkAdvancedMarkerSupport();

  if (isAdvancedMarkerSupported.value) {
    marker = new google.maps.marker.AdvancedMarkerElement({
      position: myLatLng,
      title: props.name,
      map: map
    });
  } else {
    // Fallback to legacy Marker if AdvancedMarkerElement is not available
    const legacyMarker = new google.maps.Marker({
      position: myLatLng,
      title: props.name,
    });
    legacyMarker.setMap(map);
    marker = legacyMarker as unknown as google.maps.marker.AdvancedMarkerElement;
  }
});
</script>

<template>
  <div class="event-map">
    <div :id="tagName"></div>
  </div>
</template>
<style scoped>
.event-map {
  > div {
    padding-bottom: 70%;
  }

  max-width: 400px;
}
</style>
